import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/global.css';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  locations?: string[] | null;
  pin?: string;
}

interface ProductionLine {
  id: number;
  name: string;
  location?: string | null;
}

/** Roles built into the app, in the order the role list shows them. */
const BUILT_IN_ROLES = ['admin', 'kierownik_produkcji', 'user', 'stacja_pakowania', 'tracker'];

/** A role added by the admin; its tab access is set in "Dostęp do zakładek". */
interface CustomRole {
  key: string;
  name: string;
}

interface ProductionLineLogin {
  id: number;
  production_line_id: number;
  user_id: number | null;
  login_type: 'line_selection' | 'user_login';
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  productionLine?: ProductionLine;
  user?: User | null;
}

const Users: React.FC = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const emptyForm = { name: '', email: '', password: '', role: 'user', locations: [] as string[], pin: '' };
  const [formData, setFormData] = useState(emptyForm);
  const [showLoginLogs, setShowLoginLogs] = useState(false);
  const [loginLogs, setLoginLogs] = useState<ProductionLineLogin[]>([]);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [productionLines, setProductionLines] = useState<ProductionLine[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [lineAssignmentUser, setLineAssignmentUser] = useState<User | null>(null);
  const [assignedLineIds, setAssignedLineIds] = useState<number[]>([]);
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [showRoleList, setShowRoleList] = useState(false);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [roleError, setRoleError] = useState<string | null>(null);
  const [savingRole, setSavingRole] = useState(false);

  useEffect(() => {
    Promise.all([fetchUsers(), fetchProductionLines(), fetchRoles()]).finally(() => setLoading(false));
  }, []);

  const fetchProductionLines = async () => {
    try {
      const response = await api.get('/production-lines');
      setProductionLines(response.data);
    } catch (error) {
      console.error('Error fetching production lines:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      setCustomRoles(response.data.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleOpenRoleForm = () => {
    setRoleName('');
    setRoleError(null);
    setShowRoleForm(true);
  };

  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingRole(true);
    setRoleError(null);
    try {
      const response = await api.post('/roles', { name: roleName });
      setCustomRoles((prev) => [...prev, response.data.data]);
      setShowRoleForm(false);
    } catch (error: any) {
      setRoleError(error.response?.data?.errors?.name?.[0] || error.response?.data?.message || t('users.couldNotSaveRole'));
    } finally {
      setSavingRole(false);
    }
  };

  const fetchLoginLogs = async (lineId?: number) => {
    setLoadingLogs(true);
    try {
      let response;
      if (lineId) {
        response = await api.get(`/production-lines/${lineId}/login-logs`);
        setLoginLogs(response.data);
      } else {
        response = await api.get('/production-line-logins');
        setLoginLogs(response.data.data || response.data);
      }
    } catch (error) {
      console.error('Error fetching login logs:', error);
      alert(t('users.couldNotFetchLogs'));
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleShowLoginLogs = (lineId?: number) => {
    setSelectedLineId(lineId || null);
    setShowLoginLogs(true);
    fetchLoginLogs(lineId);
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { password, ...rest } = formData;
      // On edit an empty password keeps the current one, so it is not sent at all
      const submitData = editingUser && !password ? rest : { ...rest, password };
      if (!submitData.pin || submitData.pin.length !== 6) {
        submitData.pin = '';
      }

      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, submitData);
        alert(t('users.userUpdated'));
      } else {
        await api.post('/users', submitData);
        alert(t('users.userCreated'));
      }
      fetchUsers();
      setShowForm(false);
      setEditingUser(null);
      setFormData(emptyForm);
    } catch (error: any) {
      console.error('Error saving user:', error);
      alert(t('common.error') + ': ' + (error.response?.data?.message || t('users.couldNotSave')));
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({ name: user.name, email: user.email, password: '', role: user.role, locations: user.locations || [], pin: user.pin || '' });
    setShowForm(true);
  };

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const handleOpenLineAssignment = async (user: User) => {
    setLineAssignmentUser(user);
    setAssignedLineIds([]);
    try {
      const response = await api.get(`/users/${user.id}/production-lines`);
      setAssignedLineIds(response.data.map((l: ProductionLine) => l.id));
    } catch (error) {
      console.error('Error fetching user lines:', error);
    }
  };

  const handleAssignLines = async () => {
    if (!lineAssignmentUser) return;
    try {
      await api.post(`/users/${lineAssignmentUser.id}/assign-production-lines`, {
        production_line_ids: assignedLineIds,
      });
      alert(t('users.linesAssigned'));
      setLineAssignmentUser(null);
      setAssignedLineIds([]);
    } catch (error: any) {
      alert(t('common.error') + ': ' + (error.response?.data?.message || t('users.couldNotAssignLines')));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('users.confirmDelete'))) return;
    try {
      await api.delete(`/users/${id}`);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const roleLabel = (role: string) => {
    const map: Record<string, string> = {
      admin: t('users.roleAdmin'),
      kierownik_produkcji: t('users.roleManager'),
      stacja_pakowania: t('users.rolePackaging'),
      tracker: t('users.roleTracker'),
      user: t('users.roleUser'),
    };
    return map[role] || customRoles.find((r) => r.key === role)?.name || role;
  };

  const roleBadgeStyle = (role: string): React.CSSProperties => {
    const map: Record<string, React.CSSProperties> = {
      admin: { backgroundColor: '#ede9fe', color: '#5b21b6' },
      kierownik_produkcji: { backgroundColor: '#dbeafe', color: '#1e40af' },
      stacja_pakowania: { backgroundColor: '#fef3c7', color: '#92400e' },
      tracker: { backgroundColor: '#e0e7ff', color: '#3730a3' },
      user: { backgroundColor: '#e2e8f0', color: '#4a5568' },
    };
    // Roles added by the admin share one colour.
    return map[role] || { backgroundColor: '#dcfce7', color: '#166534' };
  };

  // Locations come from the production lines; the edited user's own locations stay selectable
  // even if no line uses them anymore.
  const locationOptions = Array.from(
    new Set(
      [...productionLines.map((l) => l.location?.trim()), ...formData.locations].filter((l): l is string => !!l),
    ),
  ).sort((a, b) => a.localeCompare(b, 'pl'));

  const toggleLocation = (location: string, checked: boolean) =>
    setFormData({
      ...formData,
      locations: checked ? [...formData.locations, location] : formData.locations.filter((l) => l !== location),
    });

  return (
    <div>
      <div className="page-header" style={{ padding: '8px 0', marginBottom: 20 }}>
        <h1
          style={{
            color: '#2d3748',
            background: 'none',
            WebkitBackgroundClip: 'initial',
            WebkitTextFillColor: '#2d3748',
            backgroundClip: 'initial',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ WebkitTextFillColor: 'initial' }}>👥</span>
          {t('users.title')}
        </h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => handleShowLoginLogs()}
            className="btn"
            style={{
              backgroundColor: 'white',
              color: '#667eea',
              border: '2px solid #667eea',
              fontWeight: 600,
            }}
          >
            📊 {t('users.showLoginLogs')}
          </button>
          <button
            onClick={() => setShowRoleList(true)}
            className="btn"
            style={{
              backgroundColor: 'white',
              color: '#667eea',
              border: '2px solid #667eea',
              fontWeight: 600,
            }}
          >
            🔑 {t('users.roles')}
          </button>
          <button
            onClick={handleOpenCreate}
            className="btn"
            style={{ backgroundColor: '#667eea', color: '#ffffff', fontWeight: 600 }}
          >
            + {t('users.addUser')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner" />
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('users.name')}
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('users.email')}
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('users.role')}
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('users.locations')}
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    PIN
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('users.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        padding: '48px 20px',
                        textAlign: 'center',
                        color: '#718096',
                        fontStyle: 'italic',
                      }}
                    >
                      {t('users.empty')}
                    </td>
                  </tr>
                )}
                {users.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #e1e8ed' }}>
                    <td style={{ padding: '14px 12px', fontWeight: 600, color: '#2d3748' }}>{user.name}</td>
                    <td style={{ padding: '14px 12px', color: '#4a5568' }}>{user.email}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          ...roleBadgeStyle(user.role),
                        }}
                      >
                        {roleLabel(user.role)}
                      </span>
                    </td>
                    <td style={{ padding: '14px 12px', color: '#4a5568' }}>{user.locations?.length ? user.locations.join(', ') : '—'}</td>
                    <td
                      style={{
                        padding: '14px 12px',
                        textAlign: 'center',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        color: user.pin ? '#10b981' : '#9ca3af',
                        letterSpacing: 2,
                      }}
                    >
                      {user.pin || '—'}
                    </td>
                    <td style={{ padding: '14px 12px' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleOpenLineAssignment(user)}
                          className="btn"
                          style={{
                            padding: '6px 12px',
                            fontSize: 13,
                            backgroundColor: '#667eea',
                            color: 'white',
                          }}
                        >
                          🏭 {t('users.assignLines')}
                        </button>
                        <button
                          onClick={() => handleEdit(user)}
                          className="btn"
                          style={{
                            padding: '6px 12px',
                            fontSize: 13,
                            backgroundColor: 'white',
                            color: '#667eea',
                            border: '2px solid #667eea',
                          }}
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="btn"
                          style={{
                            padding: '6px 12px',
                            fontSize: 13,
                            backgroundColor: '#ef4444',
                            color: 'white',
                          }}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: 30,
              borderRadius: 12,
              maxWidth: 500,
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h2 style={{ marginTop: 0, color: '#667eea' }}>
              {editingUser ? t('users.editUser') : t('users.addUser')}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('users.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input"
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('users.email')} *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="input"
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('users.password')} {!editingUser && '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? t('users.passwordKeepHint') : undefined}
                  autoComplete="new-password"
                  className="input"
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('users.role')} *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="input"
                >
                  <option value="user">{t('users.roleUser')}</option>
                  <option value="kierownik_produkcji">{t('users.roleManager')}</option>
                  <option value="stacja_pakowania">{t('users.rolePackaging')}</option>
                  <option value="tracker">{t('users.roleTracker')}</option>
                  <option value="admin">{t('users.roleAdmin')}</option>
                  {customRoles.map((role) => (
                    <option key={role.key} value={role.key}>{role.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('users.locations')}
                </label>
                {locationOptions.length === 0 ? (
                  <div style={{ color: '#718096', fontStyle: 'italic', marginBottom: 4 }}>{t('users.noLocations')}</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', marginBottom: 4 }}>
                    {locationOptions.map((location) => (
                      <label key={location} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#2d3748' }}>
                        <input
                          type="checkbox"
                          checked={formData.locations.includes(location)}
                          onChange={(e) => toggleLocation(location, e.target.checked)}
                          style={{ width: 18, height: 18 }}
                        />
                        {location}
                      </label>
                    ))}
                  </div>
                )}
                <small style={{ color: '#718096', fontSize: 12 }}>{t('users.locationHint')}</small>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('users.pinLabel')}
                </label>
                <input
                  type="text"
                  value={formData.pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setFormData({ ...formData, pin: value });
                  }}
                  placeholder="000000"
                  maxLength={6}
                  className="input"
                  style={{ fontFamily: 'monospace', letterSpacing: 2 }}
                />
                <small style={{ color: '#718096', fontSize: 12 }}>{t('users.pinHint')}</small>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingUser(null);
                  }}
                  className="btn"
                  style={{ backgroundColor: '#6c757d', color: 'white' }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn"
                  style={{ backgroundColor: '#667eea', color: 'white' }}
                >
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRoleList && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: 30,
              borderRadius: 12,
              maxWidth: 560,
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <h2 style={{ margin: 0, color: '#667eea' }}>{t('users.roles')}</h2>
              <button
                onClick={handleOpenRoleForm}
                className="btn"
                style={{ backgroundColor: '#667eea', color: '#ffffff', fontWeight: 600 }}
              >
                + {t('users.addRole')}
              </button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase' }}>{t('users.roleName')}</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: 13, textTransform: 'uppercase' }}>{t('users.roleUsers')}</th>
                </tr>
              </thead>
              <tbody>
                {[...BUILT_IN_ROLES, ...customRoles.map((r) => r.key)].map((role) => (
                  <tr key={role} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px' }}>
                      <span style={{ ...roleBadgeStyle(role), padding: '4px 10px', borderRadius: 12, fontSize: 13, fontWeight: 600 }}>
                        {roleLabel(role)}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#2d3748', fontWeight: 600 }}>
                      {users.filter((u) => u.role === role).length}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ margin: '12px 0 0 0', fontSize: 12, color: '#718096' }}>{t('users.rolesHint')}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setShowRoleList(false)}
                className="btn"
                style={{ backgroundColor: '#6c757d', color: 'white' }}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRoleForm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: 30,
              borderRadius: 12,
              maxWidth: 420,
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h2 style={{ marginTop: 0, color: '#667eea' }}>{t('users.addRole')}</h2>
            <form onSubmit={handleSaveRole}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('users.roleName')} *
                </label>
                <input
                  type="text"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  required
                  maxLength={100}
                  autoFocus
                  className="input"
                />
                {roleError && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 6 }}>{roleError}</div>}
                <small style={{ color: '#718096', fontSize: 12 }}>{t('users.roleHint')}</small>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowRoleForm(false)}
                  className="btn"
                  style={{ backgroundColor: '#6c757d', color: 'white' }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={savingRole}
                  className="btn"
                  style={{ backgroundColor: '#667eea', color: 'white' }}
                >
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showLoginLogs && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: 30,
              borderRadius: 12,
              maxWidth: 1100,
              width: '95%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <h2 style={{ margin: 0, color: '#667eea' }}>📊 {t('users.loginLogsTitle')}</h2>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <select
                  value={selectedLineId || ''}
                  onChange={(e) => {
                    const lineId = e.target.value ? parseInt(e.target.value) : undefined;
                    setSelectedLineId(lineId || null);
                    fetchLoginLogs(lineId);
                  }}
                  className="input"
                  style={{ width: 'auto', minWidth: 180 }}
                >
                  <option value="">{t('users.allLines')}</option>
                  {productionLines.map((line) => (
                    <option key={line.id} value={line.id}>
                      {line.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setShowLoginLogs(false)}
                  className="btn"
                  style={{ backgroundColor: '#6c757d', color: 'white' }}
                >
                  {t('common.close')}
                </button>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loadingLogs ? (
                <div className="loading-spinner">
                  <div className="spinner" />
                </div>
              ) : (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                      <thead>
                        <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('users.dateTime')}
                          </th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('users.productionLine')}
                          </th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('users.user')}
                          </th>
                          <th style={{ padding: '12px', textAlign: 'center', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('users.type')}
                          </th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            IP
                          </th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            User Agent
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {loginLogs.length === 0 && (
                          <tr>
                            <td
                              colSpan={6}
                              style={{
                                padding: '48px 20px',
                                textAlign: 'center',
                                color: '#718096',
                                fontStyle: 'italic',
                              }}
                            >
                              {t('users.noLogs')}
                            </td>
                          </tr>
                        )}
                        {loginLogs.map((log) => (
                          <tr key={log.id} style={{ borderBottom: '1px solid #e1e8ed' }}>
                            <td style={{ padding: '12px', fontSize: 13, color: '#4a5568' }}>
                              {new Date(log.created_at).toLocaleString('pl-PL', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                              })}
                            </td>
                            <td style={{ padding: '12px', fontWeight: 600, color: '#2d3748' }}>
                              🏭 {log.productionLine?.name || `ID: ${log.production_line_id}`}
                            </td>
                            <td style={{ padding: '12px', color: '#4a5568' }}>
                              {log.user ? `${log.user.name} (${log.user.email})` : '—'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '4px 10px',
                                  borderRadius: 12,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  backgroundColor: log.login_type === 'user_login' ? '#d1fae5' : '#fef3c7',
                                  color: log.login_type === 'user_login' ? '#065f46' : '#92400e',
                                }}
                              >
                                {log.login_type === 'user_login' ? '👤 ' + t('users.userLogin') : '🏭 ' + t('users.lineSelection')}
                              </span>
                            </td>
                            <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: 12, color: '#4a5568' }}>
                              {log.ip_address || '—'}
                            </td>
                            <td style={{ padding: '12px', fontSize: 11, color: '#718096', maxWidth: 300, wordBreak: 'break-word' }}>
                              {log.user_agent || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {lineAssignmentUser && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: 30,
              borderRadius: 12,
              maxWidth: 600,
              width: '90%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h2 style={{ marginTop: 0, color: '#667eea' }}>
              {t('users.assignLinesToUser', { userName: lineAssignmentUser.name })}
            </h2>
            <div style={{ marginTop: 10, overflowY: 'auto', flex: 1 }}>
              {productionLines.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#718096', fontStyle: 'italic' }}>
                  {t('users.noLinesAvailable')}
                </div>
              ) : (
                productionLines.map((line) => (
                  <label
                    key={line.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 12px',
                      marginBottom: 6,
                      borderRadius: 8,
                      border: '1px solid #e1e8ed',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                  >
                    <input
                      type="checkbox"
                      checked={assignedLineIds.includes(line.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAssignedLineIds([...assignedLineIds, line.id]);
                        } else {
                          setAssignedLineIds(assignedLineIds.filter((id) => id !== line.id));
                        }
                      }}
                      style={{ marginRight: 12, width: 18, height: 18 }}
                    />
                    <div style={{ fontWeight: 600, color: '#2d3748' }}>🏭 {line.name}</div>
                  </label>
                ))
              )}
            </div>
            <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setLineAssignmentUser(null);
                  setAssignedLineIds([]);
                }}
                className="btn"
                style={{ backgroundColor: '#6c757d', color: 'white' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAssignLines}
                className="btn"
                style={{ backgroundColor: '#10b981', color: 'white' }}
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
